import { z } from 'zod';

export * from './statsSchema';
export * from './chartSchema';
export * from './userManagement.schema';
export * from './provinceConversion';
export * from './passwordReset';
export * from './smtp';

export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
	z.object({
		success: z.boolean(),
		message: z.string().optional(),
		data: dataSchema,
	}).strip();

export const PaginationInfoSchema = z.object({
	page: z.number(),
	limit: z.number(),
	total: z.number(),
	totalPages: z.number(),
}).strip();

export const PaginatedApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
	z.object({
		success: z.boolean(),
		data: dataSchema,
		pagination: PaginationInfoSchema,
		message: z.string().optional(),
	}).strip();

// User Schema
export const UserSchema = z.object({
	id: z.string(),
	name: z.string(),
	email: z.string().email(),
	role: z.string().optional(),
	MaCB: z.string().optional(),
	maKhoa: z.string().optional(),
}).strip();

// Auth Schemas
export const LoginRequestSchema = z.object({
	email: z.string().min(1, 'Tên đăng nhập/Email không được để trống'),
	password: z.string().min(1, 'Mật khẩu không được để trống'),
	rememberMe: z.boolean().optional(),
}).strip();

export const BetterAuthResponseSchema = z.object({
	user: UserSchema,
	token: z.string(),
	redirect: z.boolean(),
}).strip();

export const SessionSchema = z.object({
	user: UserSchema,
	session: z.object({
		id: z.string(),
		userId: z.string(),
		expiresAt: z.string(),
	}).strip(),
}).strip();





// Work History Schema
export const WorkHistorySchema = z.object({
	id: z.string(),
	maSV: z.string(),
	congTy: z.string(),
	viTri: z.string(),
	ngayBatDau: z.string(),
	ngayKetThuc: z.string().nullable(),
	moTa: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
}).strip();

// Higher Education Schema
export const HigherEducationSchema = z.object({
	id: z.string(),
	maSV: z.string(),
	truongDaiHoc: z.string(),
	chuyenNganh: z.string(),
	bacHoc: z.string(),
	ngayBatDau: z.string(),
	ngayKetThuc: z.string().nullable(),
	bangCap: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
}).strip();

// Student Schema - Matches backend API exactly
export const StudentSchema = z.object({
	maSV: z.string(),
	cccd: z.string().nullable(),
	hoTen: z.string().nullable(),
	ngaySinh: z.string().nullable(), // ISO string from API
	phai: z.string().nullable(),
	noiSinh: z.string().nullable(),
	email: z.string().nullable(),
	dienThoai: z.string().nullable(),
	diaChiHienTai: z.string().nullable(),
	linkedInUrl: z.string().nullable(),
	facebookUrl: z.string().nullable(),
	websiteCaNhan: z.string().nullable(),
	maNganh: z.string().nullable(),
	ngayTotNghiep: z.string().nullable(), // ISO string from API
	loaiBang: z.string().nullable(),
	gpa: z.number().nullable(),
	xepLoaiTotNghiep: z.string().nullable(),
	phuongThucXetTuyen: z.string().nullable(),
	khuVuc: z.string().nullable(),
	doiTuong: z.string().nullable(),
	hocLuc: z.string().nullable(),
	hanhKiem: z.string().nullable(),
	diemTrungBinhLop12: z.number().nullable(),
	maTruongTHPT: z.string().nullable(),
	diemToan: z.number().nullable(),
	diemVan: z.number().nullable(),
	diemLi: z.number().nullable(),
	diemHoa: z.number().nullable(),
	diemSinh: z.number().nullable(),
	diemSu: z.number().nullable(),
	diemDia: z.number().nullable(),
	diemGDCD: z.number().nullable(),
	diemNgoaiNgu: z.number().nullable(),
	maMonNgoaiNgu: z.string().nullable(),
	diemTruocCong: z.number().nullable(),
	diemTrungTuyen: z.number().nullable(),
	anhDaiDienUrl: z.string().nullable(),
	gioiThieu: z.string().nullable(),
	ghiChu: z.string().nullable(),
	createdAt: z.string(), // ISO string from API
	updatedAt: z.string(), // ISO string from API
	nganh: z.object({
		maNganh: z.string(),
		tenNganh: z.string(),
		maKhoa: z.string(),
		tenKhoa: z.string().optional(),
	}).strip().nullish(),
	truongTHPT: z.object({
		maTruong: z.string(),
		// Vắng mặt cho trường chỉ biết nhóm sau sáp nhập (chưa biết tỉnh gốc) —
		// xem docs/dev-guide/truong-thpt-va-nhom-tinh-sap-nhap.md.
		maTinh: z.string().optional(),
		tenTruong: z.string(),
		top149: z.boolean().optional(),
	}).strip().nullish(),
	phuongThucTuyenSinhRef: z.object({
		maPhuongThuc: z.string(),
		tenPhuongThuc: z.string().nullable(),
	}).strip().nullish(),
	lichSuCongViec: z.array(WorkHistorySchema).optional(),
	hocVanCaoHon: z.array(HigherEducationSchema).optional(),
}).strip();

// Students API Response Schemas (No success field in students list)
export const StudentsResponseSchema = z.object({
	data: z.array(StudentSchema),
	pagination: PaginationInfoSchema,
}).strip();

export const StudentDetailResponseSchema = z.object({
	success: z.boolean(),
	data: StudentSchema,
}).strip();

// Statistics Schemas
export const ScoreStatisticsSchema = z.object({
	averageTotal: z.number(),
	averageGPA: z.number(),
	averageMath: z.number(),
	averageLiterature: z.number(),
	averageEnglish: z.number(),
	maxTotal: z.number(),
	minTotal: z.number(),
	maxGPA: z.number(),
	minGPA: z.number(),
}).strip();

export const StudentsStatsResponseSchema = z.object({
	success: z.boolean(),
	data: z.object({
		totalStudents: z.number(),
		studentsThisYear: z.number(),
		studentsLastYear: z.number(),
		growthRate: z.number(),
		majorStats: z.array(z.object({
			code: z.string(),
			name: z.string(),
			faculty: z.string(),
			count: z.number(),
		}).strip()),
		admissionMethodStats: z.array(z.object({
			method: z.string(),
			count: z.number(),
		}).strip()),
		provinceStats: z.array(z.object({
			provinceName: z.string(),
			count: z.number(),
		}).strip()),
		gradeStats: z.array(z.object({
			grade: z.string(),
			count: z.number(),
		}).strip()),
		scoreStatistics: ScoreStatisticsSchema,
		monthlyTrend: z.array(z.object({
			month: z.number(),
			monthName: z.string(),
			count: z.number(),
		}).strip()),
	}).strip(),
}).strip();



// Type exports for commonly used schemas
export type User = z.infer<typeof UserSchema>;
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type BetterAuthResponse = z.infer<typeof BetterAuthResponseSchema>;
export type Session = z.infer<typeof SessionSchema>;
export type WorkHistory = z.infer<typeof WorkHistorySchema>;
export type HigherEducation = z.infer<typeof HigherEducationSchema>;
export type Student = z.infer<typeof StudentSchema>;
export type StudentsResponse = z.infer<typeof StudentsResponseSchema>;
export type StudentDetailResponse = z.infer<typeof StudentDetailResponseSchema>;
export type StudentsStatsResponse = z.infer<typeof StudentsStatsResponseSchema>;